import { FilterDateDashboard } from "../../../../components/Dashboard/ViewDatas/FilterDate";
import { ReactComponent as BagFood } from "../../../../assets/svg/icons/bagFood.svg";
import { DateModal } from "../../../../components/Modal/DateFilter";
import { useFilter } from "../../../../hooks/useFilter";
import Constants from "../../../../constants/Index";
import { useEffect, useMemo, useRef, useState } from "react";
import { MultiDropdownV2, OptionV2 } from "../../../../components/FormControlsV2";
import { Controller, useForm } from "react-hook-form";
import { format } from "date-fns";
import ChartBar from "../../../../components/Graph/Bar";
import ChartBarLine from "../../../../components/Graph/BarLine";
import "../styles.css"
import { IOptionDoubleValuesProps, IOptionProps, IOptionsProps } from "../../../../@types/reactSelect/IOption";
import axios from "../../../../services/axios";
import { LeafletStaticMap } from "../../../../components/Leaflet/LeafletStaticMap/Index";
import { FlyToBounds, FlyToBoundsRefProps } from "../../../../components/Leaflet/FlyToBounds/Index";
import { Polygon } from "react-leaflet";
import ChartBarLineAverage from "../../../../components/Graph/BarLineAverage";
import { DateRange } from "react-day-picker";
import { exportPastureManagementDataToExcel } from "../../../../utils/excel/exportPastureManagementData";
import {
    flattenSelection,
    primaryFarmIdFromSelection,
    selectionToNumericIds,
    selectionToSortedIdsKey,
    useFilterContextV2,
} from "../../../../contexts/FilterContextV2";

export default function DashboardManagementGeneral() {
    const {
        rangeDays,
        setRangeDays,
        setHideMicroAreaFilter,
        setHideBatchFilter,
        setDashboard,
        setSideBarOptionSelected,
        setSelectRangeDate,
        snapshotMicroAreaMetrics,
        setSnapshotMicroAreaMetrics,
    } = useFilter();

    const { selectedFarms, selectedAreas } = useFilterContextV2();

    const farmsKey = useMemo(
        () => selectionToSortedIdsKey(selectedFarms),
        [selectedFarms],
    );
    const areasKey = useMemo(
        () => selectionToSortedIdsKey(selectedAreas),
        [selectedAreas],
    );

    const primaryFarmId = primaryFarmIdFromSelection(selectedFarms);
    const primaryFarmCoords = useMemo(() => {
        const first = flattenSelection(selectedFarms)[0];
        const info = first?.info as { map_coords?: unknown; coords?: unknown } | undefined;
        const c = info?.map_coords ?? info?.coords;
        return Array.isArray(c) && c.length > 0 ? c : undefined;
    }, [selectedFarms]);

    const {
        handleSubmit,
        control,
        watch,
        setValue,
    } = useForm();

    const [optionsTypeManagement, setOptionsTypeManagement] = useState<IOptionsProps>([]);
    const [managementOptions, setManagementOptions] = useState<IOptionsProps>();
    const [lotInst, setLotInst] = useState<IOptionsProps>([]);
    const [consumoSuplemment, setConsumoSuplemment] = useState<IOptionDoubleValuesProps>();
    const [prodAcumulada, setProdAcumulada] = useState<IOptionsProps>([]);
    const [areas, setAreas] = useState<IArea[]>();
    const [microAreas, setMicroAreas] = useState<IMicroArea[]>();
    const [microAreasSelected, setMicroAreasSelected] = useState<Number[]>();
    const [searchDate, setSearchDate] = useState<DateRange | undefined>(undefined);

    const typeManagement = watch("managementType")
    const groupManagement = watch("managementGroup")

    useEffect(() => {
        setValue("managementGroup", [])
    }, [farmsKey, areasKey]);

    useEffect(() => {
        setHideMicroAreaFilter(true)
        setHideBatchFilter(true)
        setSideBarOptionSelected("DASHBOARD")
        setDashboard("MANAGEMENT")
        setSelectRangeDate(0)
        document.title = Constants.TITLE_DASHBOARD;

        axios
            .get("/micro-area-management/types")
            .then((response) => {
                const types: IOptionsProps = response.data.map((type: any) => ({
                    label: type.name,
                    value: type.id,
                    info: type.monitoring_types
                }))
                setOptionsTypeManagement(types.filter((type: any) => type.value != 1))
            })

        const now = new Date();
        const firstDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

        setRangeDays({
            from: firstDayOfLastMonth,
            to: firstDayOfCurrentMonth
        })

        axios.get("/micro-areas").then(response => setMicroAreas(response.data))
    }, []);

    useEffect(() => {
        if (primaryFarmId != null) {
            axios.get(`/areas?farm_id=${primaryFarmId}`).then(response => setAreas(response.data))
        }
    }, [primaryFarmId]);

    useEffect(() => {
        if (primaryFarmId == null || !rangeDays || !areas) return;

        const selectedAreaIds = selectionToNumericIds(selectedAreas);
        let startDate = format(rangeDays?.from ? new Date(rangeDays?.from) : new Date(), 'yyyy-MM-dd')
        let endDate = format(rangeDays?.to ? new Date(rangeDays?.to) : new Date(), 'yyyy-MM-dd')
        const defaultAreas = areas.map(area => area.id)
        const areaIdsForQuery = selectedAreaIds.length > 0 ? selectedAreaIds : defaultAreas;

        axios.get(`/analytics/snapshots/management-groups?startDate=${startDate}&endDate=${endDate}&farmId=${primaryFarmId}&${areaIdsForQuery.map(area => `areaIds=${area}`).join("&")}`)
            .then(response => setManagementOptions(response.data.map((option: IAnalyticsManagementGroups) => ({
                label: `${option.management_type.name} - ${option.management_type.monitoring_types.map(el => el.name).join(",")}`,
                value: option.management_group_id,
                info: option
            }))))
    }, [primaryFarmId, rangeDays, areas, farmsKey, areasKey]);

    useEffect(() => {
        if (!rangeDays || !groupManagement || groupManagement.length === 0) return;

        const controller = new AbortController();

        const fetchMetrics = async () => {
            const startDate = format(rangeDays.from ? new Date(rangeDays.from) : new Date(), 'yyyy-MM-dd');
            const endDate = format(rangeDays.to ? new Date(rangeDays.to) : new Date(), 'yyyy-MM-dd');

            const microAreas = groupManagement
                .map((management: IOptionProps) =>
                    (management.info as IAnalyticsManagementGroups).micro_areas.map(microArea => microArea.id)
                )
                .flat();

            if (microAreas.length === 0) {
                setSnapshotMicroAreaMetrics(undefined as any);
                return;
            }

            try {
                const microAreaParams = microAreas.map((microArea: number) => `microAreaIds=${microArea}`).join("&");
                const response = await axios.get(
                    `/analytics/snapshots/micro-areas/metrics?startDate=${startDate}&endDate=${endDate}&${microAreaParams}`,
                    { signal: controller.signal }
                );
                setSnapshotMicroAreaMetrics(response.data);
            } catch (error) {
                if (!controller.signal.aborted) {
                    console.error('Erro ao carregar métricas de manejo:', error);
                }
            }
        };

        fetchMetrics();

        return () => controller.abort();
    }, [rangeDays, groupManagement]);

    useEffect(() => {
        if (snapshotMicroAreaMetrics) {
            setLotInst(snapshotMicroAreaMetrics.daily_metrics.map((inst) => ({
                label: inst.date,
                value: inst.average_instant_capacity_rate
            })))

            setConsumoSuplemment({
                data1: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.average_consumption_kg_per_animal
                })),
                data2: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.average_consumption_g_per_kg_pv
                }))
            })

            setProdAcumulada(snapshotMicroAreaMetrics.daily_metrics.map((inst) => ({
                label: inst.date,
                value: inst.productivity
            })))
        }
    }, [snapshotMicroAreaMetrics]);

    const renderManagementGroupOption = (option: OptionV2) => {
        const infoOption: IAnalyticsManagementGroups = option.info;

        return (
            <div className="customDropdownOptions">
                {infoOption.management_type.name} - {infoOption.management_type.monitoring_types.map(el => el.name).join(",")}
                <br />
                {infoOption.micro_areas.length > 0 ? infoOption.micro_areas.map(microArea => microArea.initials).join(" ") : ""}
            </div>
        );
    }

    const flytoBoundsRef = useRef<FlyToBoundsRefProps>();

    useEffect(() => {
        if (groupManagement && groupManagement.length > 0) setMicroAreasSelected(groupManagement.map((management: IOptionProps) => (management.info as IAnalyticsManagementGroups).micro_areas.map(microArea => microArea.id)).flat())
    }, [groupManagement]);

    return (
        <div className="cattleMap_background" style={{ height: "100vh", overflowY: "auto" }}>
            <DateModal />

            <form onSubmit={() => handleSubmit(() => { })} className="dashboardPastureManagement">
                <div className="cattleMap_page">
                    <div className="cattleMap_header">
                        <BagFood height={72} />
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                        }}>
                            <h2 className="cattleMap_title">
                                Manejo de Pasto
                            </h2>
                            <h3 className="cattleMap_subtitle">
                                Visualização Geral
                            </h3>
                        </div>
                    </div>

                    <div>
                        <FilterDateDashboard
                            onExportExcel={() => {
                                if (snapshotMicroAreaMetrics) {
                                    try {
                                        exportPastureManagementDataToExcel(
                                            snapshotMicroAreaMetrics,
                                            rangeDays?.from ? new Date(rangeDays.from) : undefined,
                                            rangeDays?.to ? new Date(rangeDays.to) : undefined
                                        );
                                    } catch (error) {
                                        console.error('Erro ao exportar:', error);
                                        alert('Erro ao exportar dados. Tente novamente.');
                                    }
                                }
                            }}
                        />
                        <br />
                        <div style={{ width: "100%", maxWidth: 400 }}>
                            <Controller
                                name="managementType"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <MultiDropdownV2
                                        name="managementType"
                                        label="Tipo de pastejo"
                                        placeholder="Selecione..."
                                        menuMount="static"
                                        emptyMessage="Nenhuma opção disponível"
                                        options={optionsTypeManagement}
                                        value={field.value ?? []}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        error={error?.message}
                                    />
                                )}
                            />

                            <Controller
                                name="managementGroup"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <MultiDropdownV2
                                        name="managementGroup"
                                        label="Grupo de manejo"
                                        placeholder="Selecione..."
                                        menuMount="static"
                                        emptyMessage="Nenhuma opção disponível"
                                        options={managementOptions || []}
                                        value={field.value ?? []}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        error={error?.message}
                                        renderOption={renderManagementGroupOption}
                                    />
                                )}
                            />

                            {primaryFarmCoords && primaryFarmCoords.length > 0 && <div className="mapFilterManagement">
                                <LeafletStaticMap>
                                    <FlyToBounds ref={flytoBoundsRef} fitCoords={primaryFarmCoords}>
                                        <Polygon
                                            dashArray={Constants.LEAFLET_DASHED_GREEN.dashArray}
                                            color={Constants.LEAFLET_DASHED_GREEN.color}
                                            fill={false}
                                            positions={primaryFarmCoords}
                                        />
                                        {/* microArea */}
                                        {microAreas?.filter(microarea => microAreasSelected?.includes(microarea.id))?.map(microarea => <Polygon
                                            positions={microarea.map_coords!}
                                            color={Constants.LEAFLET_UNFOCUS_TRANSLUCENT.color}
                                            fillColor={Constants.LEAFLET_NORMAL_BLUE.fillColor}
                                            fillOpacity={
                                                Constants.LEAFLET_UNFOCUS_TRANSLUCENT.fillOpacity
                                            }
                                        />)}

                                    </FlyToBounds>
                                </LeafletStaticMap>
                            </div>
                            }
                        </div>
                    </div>
                </div>

                <br />
                <div className="splitContent">
                    {/* <ChartBarLineAverage
                            titleGraph={"Taxa de lotação instantânea"}
                            dataColumn={lotInst}
                            average={lotInst.length > 0 ? lotInst.reduce((acc, item) => acc + item.value, 0) / lotInst.length : 0}
                            colorIcons={["Tx lotação instantânea", "Média"]}
                            axisLabels={"UA/ha"}
                            fullWidth
                        /> */}
                    <div style={{
                        overflowX: "auto",
                        width: "100%"
                    }}>
                        <ChartBar
                            titleGraph={"Taxa de lotação instantânea"}
                            data={lotInst}
                            axisLabels={["", "UA/ha"]}
                            fullWidth
                            monoColor
                        />
                    </div>
                    {lotInst.length > 0 && !lotInst?.every((el: any) => el?.value === 0) ?
                        <div className="contentGraphBorder" style={{ minWidth: 200, maxWidth: 980, marginTop: 21, flexDirection: "column" }}>
                            <span className="infoMediaContainer">
                                Média da taxa de lotação no período
                                <br />
                                <br />
                                {(lotInst.reduce((acc, item) => acc + item.value, 0) / lotInst.length).toFixed(2)} UA/ha
                            </span>
                        </div>
                        : <></>
                    }
                </div>

                <div className="splitContent">
                    <div style={{
                        overflowX: "auto",
                        width: "100%"
                    }}>
                        <ChartBarLine
                            titleGraph={"Consumo de suplemento"}
                            dataColumn={consumoSuplemment?.data1 || []}
                            dataLine={consumoSuplemment?.data2 || []}
                            colorIcons={["Consumo médio no período (g/kgPV.dia)", "Consumo médio no período (kg/cab.dia)"]}
                            axisLabels={["", "g/kgPV.dia", "kg/cab.dia"]}
                            fullWidth
                        />
                    </div>
                    {consumoSuplemment && !consumoSuplemment?.data1.every((el: any) => el?.value === 0) && !consumoSuplemment?.data2.every((el: any) => el?.value === 0) ?
                        <div className="contentGraphBorder" style={{ minWidth: 200, maxWidth: 980, marginTop: 21, flexDirection: "column" }}>
                            <span className="infoMediaContainer">
                                Média de consumo
                                <br />
                                no período
                                <br />
                                <br />
                                {!consumoSuplemment?.data1.every((el: any) => el?.value === 0) ? `${(consumoSuplemment?.data1.reduce((acc, item) => acc + item.value, 0) / consumoSuplemment?.data1.length).toFixed(2)} g/kgPV.dia` : <></>}
                                <br />
                                {!consumoSuplemment?.data1.every((el: any) => el?.value === 0) ? `${(consumoSuplemment?.data2.reduce((acc, item) => acc + item.value, 0) / consumoSuplemment?.data2.length).toFixed(2)} kg/cab.dia` : <></>}
                            </span>
                        </div>
                        : <></>
                    }
                </div>

                <div className="splitContent">
                    <div style={{
                        overflowX: "auto",
                        width: "100%"
                    }}>
                        <ChartBar
                            titleGraph={"Produtividade acumulada"}
                            data={prodAcumulada}
                            // colorIcons={"Produtividade acumulada"}
                            axisLabels={["", "Kg PV/ha"]}
                            fullWidth
                            monoColor
                        />
                    </div>
                    {prodAcumulada.length > 0 && !prodAcumulada?.every((el: any) => el?.value === 0) ?
                        <div className="contentGraphBorder" style={{ minWidth: 200, maxWidth: 980, marginTop: 21, flexDirection: "column" }}>
                            <span className="infoMediaContainer">
                                Total de Produtividade acumulada no período
                                <br />
                                <br />
                                {(prodAcumulada.reduce((acc, item) => acc + item.value, 0)).toFixed(2)} Kg PV/ha
                            </span>
                        </div>
                        : <></>
                    }
                </div>
            </form >
        </div >
    );
}
